import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { loadCompanyProfile } from '../settings/CompanyProfileModal';
import { loadSavedClients } from '../settings/ClientManager';
import { exportInvoiceToPDF } from '../../utils/exportPDF';

/**
 * Generate unique ID for line items
 */
const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * Get/Set the next invoice number from localStorage
 */
const INVOICE_NUMBER_KEY = 'floorplan_next_invoice_number';
const DEFAULT_STARTING_NUMBER = 1001;

export const getNextInvoiceNumber = () => {
  const stored = localStorage.getItem(INVOICE_NUMBER_KEY);
  return stored ? parseInt(stored, 10) : DEFAULT_STARTING_NUMBER;
};

export const incrementInvoiceNumber = () => {
  const current = getNextInvoiceNumber();
  localStorage.setItem(INVOICE_NUMBER_KEY, String(current + 1));
  return current;
};

export const setStartingInvoiceNumber = (num) => {
  localStorage.setItem(INVOICE_NUMBER_KEY, String(num));
};

/**
 * Invoice status options
 */
const INVOICE_STATUSES = {
  draft: { label: 'Draft', color: '#6080a0' },
  sent: { label: 'Sent', color: '#f0a030' },
  paid: { label: 'Paid', color: '#40c060' },
};

/**
 * Payment terms options
 */
const PAYMENT_TERMS = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_45', label: 'Net 45' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'custom', label: 'Custom' },
];

/**
 * StatusDropdown - Custom dropdown for status selection with proper dark theme support
 */
const StatusDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const currentStatus = options[value] || options.draft;

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: currentStatus.color,
          padding: '6px 24px 6px 10px',
          borderRadius: '6px',
          fontSize: '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          position: 'relative',
        }}
      >
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: currentStatus.color,
        }} />
        {currentStatus.label}
        <span style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: `translateY(-50%) rotate(${isOpen ? '180deg' : '0deg'})`,
          fontSize: '10px',
          color: '#6080a0',
          transition: 'transform 0.15s',
        }}>▼</span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown menu */}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: '#1a2332',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '6px',
            overflow: 'hidden',
            zIndex: 1000,
            minWidth: '120px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          }}>
            {Object.entries(options).map(([key, opt]) => (
              <div
                key={key}
                onClick={() => {
                  onChange(key);
                  setIsOpen(false);
                }}
                onMouseEnter={() => setHoveredOption(key)}
                onMouseLeave={() => setHoveredOption(null)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: hoveredOption === key
                    ? 'rgba(0,136,255,0.2)'
                    : value === key
                      ? 'rgba(255,255,255,0.05)'
                      : 'transparent',
                  color: opt.color,
                  fontSize: '12px',
                  transition: 'background 0.1s',
                }}
              >
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: opt.color,
                }} />
                {opt.label}
                {value === key && (
                  <span style={{ marginLeft: 'auto', fontSize: '10px' }}>✓</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * InvoiceEditor - Full-screen editor for creating and editing invoices
 */
const InvoiceEditor = ({
  isOpen,
  onClose,
  onSave,
  existingInvoice = null,
  sourceEstimate = null, // When converting from estimate
  projectName = 'Floor Plan',
  isMobile = false,
}) => {
  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceName, setInvoiceName] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('net_30');
  const [customPaymentTerms, setCustomPaymentTerms] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [laborItems, setLaborItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [showTax, setShowTax] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [status, setStatus] = useState('draft');
  const [savedClients, setSavedClients] = useState([]);

  // Load company profile and saved clients
  useEffect(() => {
    if (isOpen) {
      const profile = loadCompanyProfile();
      setCompanyProfile(profile);
      setSavedClients(loadSavedClients());
    }
  }, [isOpen]);

  // Handle selecting a saved client
  const handleSelectClient = (clientId) => {
    if (!clientId) return;
    const client = savedClients.find(c => c.id === clientId);
    if (client) {
      setClientName(client.name || '');
      setClientEmail(client.email || '');
      setClientPhone(client.phone || '');
      setClientAddress(client.address || '');
    }
  };

  // Calculate due date based on payment terms
  const calculateDueDate = (fromDate, terms) => {
    const date = new Date(fromDate + 'T00:00:00');
    switch (terms) {
      case 'due_on_receipt':
        return fromDate;
      case 'net_15':
        date.setDate(date.getDate() + 15);
        break;
      case 'net_30':
        date.setDate(date.getDate() + 30);
        break;
      case 'net_45':
        date.setDate(date.getDate() + 45);
        break;
      case 'net_60':
        date.setDate(date.getDate() + 60);
        break;
      default:
        date.setDate(date.getDate() + 30);
    }
    return date.toISOString().split('T')[0];
  };

  // Initialize from existing invoice or source estimate
  useEffect(() => {
    if (!isOpen) return;

    const today = new Date().toISOString().split('T')[0];

    // Default terms text for invoices
    const defaultInvoiceTerms = `• Payment due as per terms above
• Late payments may be subject to fees
• Make checks payable to company name
• Thank you for your business!`;

    if (existingInvoice) {
      // Load existing invoice
      setInvoiceNumber(existingInvoice.invoiceNumber || '');
      setInvoiceName(existingInvoice.name || '');
      setClientName(existingInvoice.clientName || '');
      setClientEmail(existingInvoice.clientEmail || '');
      setClientPhone(existingInvoice.clientPhone || '');
      setClientAddress(existingInvoice.clientAddress || '');
      setInvoiceDate(existingInvoice.invoiceDate || today);
      setDueDate(existingInvoice.dueDate || '');
      setPaymentTerms(existingInvoice.paymentTerms || 'net_30');
      setCustomPaymentTerms(existingInvoice.customPaymentTerms || '');
      setScopeOfWork(existingInvoice.scopeOfWork || '');
      setTermsAndConditions(existingInvoice.termsAndConditions || '');
      setLineItems(existingInvoice.lineItems || []);
      setLaborItems(existingInvoice.laborItems || []);
      setTaxRate(existingInvoice.taxRate || 0);
      setShowTax(existingInvoice.taxRate > 0);
      setStatus(existingInvoice.status || 'draft');
    } else if (sourceEstimate) {
      // Converting from estimate - get new invoice number
      const newInvoiceNum = incrementInvoiceNumber();
      setInvoiceNumber(String(newInvoiceNum));
      setInvoiceName(sourceEstimate.name?.replace('Estimate', 'Invoice') || `${projectName} - Invoice`);
      setClientName(sourceEstimate.clientName || '');
      setClientEmail(sourceEstimate.clientEmail || '');
      setClientPhone(sourceEstimate.clientPhone || '');
      setClientAddress(sourceEstimate.clientAddress || '');
      setInvoiceDate(today);
      setDueDate(calculateDueDate(today, 'net_30'));
      setPaymentTerms('net_30');
      setCustomPaymentTerms('');
      setScopeOfWork(sourceEstimate.scopeOfWork || '');
      setTermsAndConditions(defaultInvoiceTerms);
      setLineItems(sourceEstimate.lineItems || []);
      setLaborItems(sourceEstimate.laborItems || []);
      setTaxRate(sourceEstimate.taxRate || 0);
      setShowTax(sourceEstimate.taxRate > 0);
      setStatus('draft');
    } else {
      // New invoice
      const newInvoiceNum = incrementInvoiceNumber();
      setInvoiceNumber(String(newInvoiceNum));
      setInvoiceName(`${projectName} - Invoice`);
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setClientAddress('');
      setInvoiceDate(today);
      setDueDate(calculateDueDate(today, 'net_30'));
      setPaymentTerms('net_30');
      setCustomPaymentTerms('');
      setScopeOfWork('');
      setTermsAndConditions(defaultInvoiceTerms);
      setLineItems([]);
      setLaborItems([]);
      setTaxRate(0);
      setShowTax(false);
      setStatus('draft');
    }
  }, [isOpen, existingInvoice, sourceEstimate, projectName]);

  // Update due date when invoice date or payment terms change
  useEffect(() => {
    if (invoiceDate && paymentTerms !== 'custom') {
      setDueDate(calculateDueDate(invoiceDate, paymentTerms));
    }
  }, [invoiceDate, paymentTerms]);

  if (!isOpen) return null;

  // Calculate totals
  const materialsSubtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const laborSubtotal = laborItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const subtotal = materialsSubtotal + laborSubtotal;
  const taxAmount = showTax ? subtotal * (taxRate / 100) : 0;
  const total = subtotal + taxAmount;

  // Handle line item changes
  const updateLineItem = (id, field, value) => {
    setLineItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const deleteLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      id: generateId(),
      name: '',
      description: '',
      quantity: 1,
      unit: 'ea',
      unitPrice: 0,
    }]);
  };

  // Handle labor item changes
  const updateLaborItem = (id, field, value) => {
    setLaborItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const deleteLaborItem = (id) => {
    setLaborItems(prev => prev.filter(item => item.id !== id));
  };

  const addLaborItem = () => {
    setLaborItems(prev => [...prev, {
      id: generateId(),
      description: '',
      amount: 0,
    }]);
  };

  // Handle save
  const handleSave = () => {
    const invoice = {
      id: existingInvoice?.id || generateId(),
      invoiceNumber,
      name: invoiceName,
      clientName,
      clientEmail,
      clientPhone,
      clientAddress,
      invoiceDate,
      dueDate,
      paymentTerms,
      customPaymentTerms: paymentTerms === 'custom' ? customPaymentTerms : '',
      scopeOfWork,
      termsAndConditions,
      lineItems,
      laborItems,
      taxRate: showTax ? taxRate : 0,
      materialsSubtotal,
      laborSubtotal,
      subtotal,
      taxAmount,
      total,
      status,
      sourceEstimateId: sourceEstimate?.id || existingInvoice?.sourceEstimateId || null,
      createdAt: existingInvoice?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave?.(invoice);
    onClose();
  };

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const paymentTermsDisplay = paymentTerms === 'custom'
      ? customPaymentTerms
      : PAYMENT_TERMS.find(t => t.value === paymentTerms)?.label || 'Net 30';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${invoiceNumber}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
            .company { }
            .company-logo { max-width: 120px; max-height: 60px; margin-bottom: 8px; }
            .company-name { font-size: 20px; font-weight: 700; color: #1a2332; }
            .company-info { font-size: 11px; color: #666; line-height: 1.5; }
            .invoice-title { font-size: 28px; font-weight: 300; color: #0066cc; text-align: right; }
            .invoice-number { font-size: 14px; color: #333; text-align: right; margin-top: 8px; font-weight: 600; }
            .invoice-date { font-size: 12px; color: #666; text-align: right; margin-top: 4px; }
            .client-section { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .client-label { font-size: 10px; text-transform: uppercase; color: #666; margin-bottom: 4px; }
            .client-name { font-size: 16px; font-weight: 600; }
            .client-details { font-size: 12px; color: #666; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th { background: #0066cc; color: #fff; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
            th:last-child { text-align: right; }
            td { padding: 12px; border-bottom: 1px solid #eee; font-size: 13px; }
            td:last-child { text-align: right; }
            .item-name { font-weight: 500; }
            .item-desc { font-size: 11px; color: #666; }
            .section-title { font-size: 14px; font-weight: 600; color: #1a2332; margin: 30px 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #0066cc; }
            .subtotal-row { display: flex; justify-content: space-between; padding: 10px 12px; background: #f8f9fa; border-radius: 4px; margin-bottom: 20px; font-weight: 500; }
            .totals { margin-left: auto; width: 300px; margin-top: 20px; }
            .total-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            .total-row.grand { font-size: 18px; font-weight: 700; border-bottom: 2px solid #0066cc; border-top: 2px solid #0066cc; background: #e6f0ff; padding: 12px; margin-top: 8px; }
            .payment-info { margin-top: 30px; padding: 20px; background: #e6f0ff; border-radius: 8px; border-left: 4px solid #0066cc; }
            .payment-label { font-size: 10px; text-transform: uppercase; color: #0066cc; margin-bottom: 8px; font-weight: 600; }
            .payment-terms { font-size: 14px; color: #333; }
            .notes { margin-top: 40px; padding: 20px; background: #f8f9fa; border-radius: 8px; }
            .notes-label { font-size: 10px; text-transform: uppercase; color: #666; margin-bottom: 8px; }
            .notes-text { font-size: 12px; color: #666; white-space: pre-wrap; }
            .footer { margin-top: 60px; text-align: center; font-size: 11px; color: #999; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              ${companyProfile?.logo ? `<img src="${companyProfile.logo}" class="company-logo" />` : ''}
              <div class="company-name">${companyProfile?.companyName || 'Your Company'}</div>
              <div class="company-info">
                ${companyProfile?.phone ? `${companyProfile.phone}<br>` : ''}
                ${companyProfile?.email ? `${companyProfile.email}<br>` : ''}
                ${companyProfile?.address ? companyProfile.address.replace(/\n/g, '<br>') : ''}
                ${companyProfile?.website ? `<br>${companyProfile.website}` : ''}
                ${companyProfile?.licenseNumber ? `<br>${companyProfile.licenseNumber}` : ''}
              </div>
            </div>
            <div>
              <div class="invoice-title">INVOICE</div>
              <div class="invoice-number">#${invoiceNumber}</div>
              <div class="invoice-date">Date: ${invoiceDate ? new Date(invoiceDate + 'T00:00:00').toLocaleDateString() : new Date().toLocaleDateString()}</div>
              <div class="invoice-date">Due: ${dueDate ? new Date(dueDate + 'T00:00:00').toLocaleDateString() : ''}</div>
            </div>
          </div>

          ${invoiceName ? `<h2 style="font-size: 18px; font-weight: 600; color: #1a2332; margin-bottom: 20px;">${invoiceName}</h2>` : ''}

          ${clientName ? `
          <div class="client-section">
            <div class="client-label">Bill To</div>
            <div class="client-name">${clientName}</div>
            <div class="client-details">
              ${clientAddress ? clientAddress.replace(/\n/g, '<br>') : ''}
              ${clientPhone ? `<br>${clientPhone}` : ''}
              ${clientEmail ? `<br>${clientEmail}` : ''}
            </div>
          </div>
          ` : ''}

          ${lineItems.length > 0 ? `
          <h3 class="section-title">Materials</h3>
          <table>
            <thead>
              <tr>
                <th style="width: 40%">Item</th>
                <th style="width: 15%">Qty</th>
                <th style="width: 15%">Unit</th>
                <th style="width: 15%">Unit Price</th>
                <th style="width: 15%">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lineItems.map(item => `
                <tr>
                  <td>
                    <div class="item-name">${item.name}</div>
                    ${item.description ? `<div class="item-desc">${item.description}</div>` : ''}
                  </td>
                  <td>${item.quantity}</td>
                  <td>${item.unit}</td>
                  <td>$${item.unitPrice.toFixed(2)}</td>
                  <td>$${(item.quantity * item.unitPrice).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="subtotal-row">
            <span>Materials Subtotal</span>
            <span>$${materialsSubtotal.toFixed(2)}</span>
          </div>
          ` : ''}

          ${laborItems.length > 0 ? `
          <h3 class="section-title">Labor</h3>
          <table>
            <thead>
              <tr>
                <th style="width: 70%">Description</th>
                <th style="width: 30%">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${laborItems.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td>$${(item.amount || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="subtotal-row">
            <span>Labor Subtotal</span>
            <span>$${laborSubtotal.toFixed(2)}</span>
          </div>
          ` : ''}

          <div class="totals">
            <div class="total-row">
              <span>Subtotal</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            ${showTax && taxRate > 0 ? `
            <div class="total-row">
              <span>Tax (${taxRate}%)</span>
              <span>$${taxAmount.toFixed(2)}</span>
            </div>
            ` : ''}
            <div class="total-row grand">
              <span>Amount Due</span>
              <span>$${total.toFixed(2)}</span>
            </div>
          </div>

          <div class="payment-info">
            <div class="payment-label">Payment Terms</div>
            <div class="payment-terms">${paymentTermsDisplay}</div>
            ${dueDate ? `<div class="payment-terms" style="margin-top: 4px;">Due Date: ${new Date(dueDate + 'T00:00:00').toLocaleDateString()}</div>` : ''}
          </div>

          ${scopeOfWork ? `
          <div class="notes">
            <div class="notes-label">Description of Work</div>
            <div class="notes-text">${scopeOfWork}</div>
          </div>
          ` : ''}

          ${termsAndConditions ? `
          <div class="notes" style="margin-top: 20px;">
            <div class="notes-label">Terms & Conditions</div>
            <div class="notes-text">${termsAndConditions}</div>
          </div>
          ` : ''}

          <div class="footer">
            Thank you for your business!
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    const invoiceData = {
      invoiceNumber,
      name: invoiceName,
      clientName,
      clientEmail,
      clientPhone,
      clientAddress,
      invoiceDate,
      dueDate,
      paymentTerms,
      customPaymentTerms,
      lineItems,
      laborItems,
      materialsSubtotal,
      laborSubtotal,
      subtotal,
      taxRate: showTax ? taxRate : 0,
      taxAmount,
      total,
      scopeOfWork,
      termsAndConditions,
    };

    try {
      await exportInvoiceToPDF(invoiceData, { companyProfile });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={{
        ...styles.modal,
        width: isMobile ? '100%' : '900px',
        height: isMobile ? '100%' : '90vh',
        borderRadius: isMobile ? 0 : '12px',
      }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: '20px' }}>🧾</span>
            <span style={styles.headerTitle}>Invoice Editor</span>
            <span style={styles.invoiceNumberBadge}>#{invoiceNumber}</span>
          </div>
          <div style={styles.headerRight}>
            {/* Status selector */}
            <StatusDropdown
              value={status}
              onChange={setStatus}
              options={INVOICE_STATUSES}
            />
            <button style={styles.exportButton} onClick={handleExportPDF}>
              📄 Export PDF
            </button>
            <button style={styles.printButton} onClick={handlePrint}>
              🖨️ Print
            </button>
            <button style={styles.closeButton} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Left Column - Line Items */}
          <div style={styles.leftColumn}>
            {/* Invoice Name */}
            <div style={styles.field}>
              <label style={styles.label}>Invoice Title</label>
              <input
                type="text"
                value={invoiceName}
                onChange={(e) => setInvoiceName(e.target.value)}
                style={styles.input}
                placeholder="Enter invoice title..."
              />
            </div>

            {/* Line Items */}
            <div style={styles.lineItemsSection}>
              <div style={styles.lineItemsHeader}>
                <span style={styles.sectionTitle}>Line Items</span>
                <button style={styles.addButton} onClick={addLineItem}>
                  + Add Item
                </button>
              </div>

              {/* Column Headers */}
              <div style={styles.columnHeaders}>
                <span style={{ flex: 2 }}>Item</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Qty</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Unit</span>
                <span style={{ width: '80px', textAlign: 'right' }}>Price</span>
                <span style={{ width: '90px', textAlign: 'right' }}>Total</span>
                <span style={{ width: '32px' }}></span>
              </div>

              {/* Line Items List */}
              <div style={styles.lineItemsList}>
                {lineItems.length === 0 ? (
                  <div style={styles.emptyState}>
                    No items yet. Add line items manually.
                  </div>
                ) : (
                  lineItems.map(item => (
                    <div key={item.id} style={styles.lineItem}>
                      <div style={{ flex: 2 }}>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                          style={styles.itemInput}
                          placeholder="Item name"
                        />
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                          style={styles.descInput}
                          placeholder="Description (optional)"
                        />
                      </div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                        style={{ ...styles.numberInput, width: '60px' }}
                        min="0"
                      />
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => updateLineItem(item.id, 'unit', e.target.value)}
                        style={{ ...styles.itemInput, width: '60px', textAlign: 'center' }}
                      />
                      <div style={{ width: '80px', position: 'relative' }}>
                        <span style={styles.dollarSign}>$</span>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                          style={styles.priceInput}
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.lineTotal}>
                        ${(item.quantity * item.unitPrice).toFixed(2)}
                      </div>
                      <button
                        style={styles.deleteButton}
                        onClick={() => deleteLineItem(item.id)}
                        title="Delete item"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Materials Subtotal */}
            {lineItems.length > 0 && (
              <div style={styles.sectionSubtotal}>
                <span>Materials Subtotal</span>
                <span>${materialsSubtotal.toFixed(2)}</span>
              </div>
            )}

            {/* Labor Section */}
            <div style={styles.laborSection}>
              <div style={styles.lineItemsHeader}>
                <span style={styles.sectionTitle}>Labor</span>
                <button style={styles.addButton} onClick={addLaborItem}>
                  + Add Labor
                </button>
              </div>

              {laborItems.length > 0 && (
                <>
                  {/* Labor Column Headers */}
                  <div style={styles.laborColumnHeaders}>
                    <span style={{ flex: 1 }}>Description</span>
                    <span style={{ width: '120px', textAlign: 'right' }}>Amount</span>
                    <span style={{ width: '32px' }}></span>
                  </div>

                  {/* Labor Items List */}
                  <div style={styles.laborItemsList}>
                    {laborItems.map(item => (
                      <div key={item.id} style={styles.laborItem}>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLaborItem(item.id, 'description', e.target.value)}
                          style={{ ...styles.itemInput, flex: 1 }}
                          placeholder="e.g., Demo and removal, Installation, Cleanup"
                        />
                        <div style={{ width: '120px', position: 'relative' }}>
                          <span style={styles.dollarSign}>$</span>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => updateLaborItem(item.id, 'amount', parseFloat(e.target.value) || 0)}
                            style={styles.laborPriceInput}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <button
                          style={styles.deleteButton}
                          onClick={() => deleteLaborItem(item.id)}
                          title="Delete labor item"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Labor Subtotal */}
                  <div style={styles.sectionSubtotal}>
                    <span>Labor Subtotal</span>
                    <span>${laborSubtotal.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Totals */}
            <div style={styles.totalsSection}>
              <div style={styles.totalRow}>
                <span>Subtotal (Materials + Labor)</span>
                <span style={styles.totalValue}>${subtotal.toFixed(2)}</span>
              </div>
              <div style={styles.taxRow}>
                <label style={styles.taxLabel}>
                  <input
                    type="checkbox"
                    checked={showTax}
                    onChange={(e) => setShowTax(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span>Add Tax</span>
                </label>
                {showTax && (
                  <>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      style={styles.taxInput}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span style={{ color: '#6080a0' }}>%</span>
                    <span style={styles.taxAmount}>${taxAmount.toFixed(2)}</span>
                  </>
                )}
              </div>
              <div style={styles.grandTotalRow}>
                <span>Amount Due</span>
                <span style={styles.grandTotalValue}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Invoice Info */}
          <div style={styles.rightColumn}>
            {/* Invoice Details */}
            <div style={styles.sectionTitle}>Invoice Details</div>

            <div style={styles.field}>
              <label style={styles.label}>Invoice Number</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Invoice Date</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Payment Terms</label>
              <select
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                style={styles.input}
              >
                {PAYMENT_TERMS.map(term => (
                  <option key={term.value} value={term.value}>{term.label}</option>
                ))}
              </select>
            </div>

            {paymentTerms === 'custom' && (
              <div style={styles.field}>
                <label style={styles.label}>Custom Terms</label>
                <input
                  type="text"
                  value={customPaymentTerms}
                  onChange={(e) => setCustomPaymentTerms(e.target.value)}
                  style={styles.input}
                  placeholder="e.g., 50% upfront, 50% on completion"
                />
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={styles.input}
              />
            </div>

            {/* Client Information */}
            <div style={{ ...styles.sectionTitle, marginTop: '20px' }}>Bill To</div>

            {/* Client selector dropdown */}
            {savedClients.length > 0 && (
              <div style={styles.field}>
                <label style={styles.label}>Select Client</label>
                <select
                  onChange={(e) => handleSelectClient(e.target.value)}
                  style={styles.clientSelect}
                  value=""
                >
                  <option value="">-- Select saved client --</option>
                  {savedClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}{client.email ? ` (${client.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                style={styles.input}
                placeholder="Client name"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                style={styles.input}
                placeholder="client@email.com"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                style={styles.input}
                placeholder="(555) 123-4567"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Address</label>
              <textarea
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                style={styles.textarea}
                placeholder="Client address"
                rows={2}
              />
            </div>

            {/* Notes Section */}
            <div style={{ ...styles.sectionTitle, marginTop: '20px' }}>Notes</div>

            <div style={styles.field}>
              <label style={styles.label}>Description of Work</label>
              <textarea
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                style={{ ...styles.textarea, minHeight: '60px' }}
                placeholder="Brief description of completed work..."
                rows={2}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Terms & Notes</label>
              <textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                style={{ ...styles.textarea, minHeight: '60px' }}
                placeholder="Payment instructions, notes..."
                rows={2}
              />
            </div>

            {/* Company Preview */}
            {companyProfile?.companyName && (
              <div style={styles.companyPreview}>
                <div style={styles.previewLabel}>From</div>
                <div style={styles.previewCompany}>{companyProfile.companyName}</div>
                {companyProfile.licenseNumber && (
                  <div style={styles.previewLicense}>{companyProfile.licenseNumber}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            style={{ background: '#0066cc', color: '#fff' }}
          >
            Save Invoice
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: '#1a2332',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,102,204,0.1)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerTitle: {
    color: '#0088ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  invoiceNumberBadge: {
    background: 'rgba(0,102,204,0.2)',
    color: '#0088ff',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  exportButton: {
    background: 'rgba(0,102,204,0.2)',
    border: '1px solid rgba(0,102,204,0.4)',
    color: '#0088ff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  printButton: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#c0d0e0',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  closeButton: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '4px',
  },
  content: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  leftColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    borderRight: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  rightColumn: {
    width: '280px',
    padding: '20px',
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.2)',
  },
  field: {
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    fontSize: '11px',
    color: '#6080a0',
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
  },
  sectionTitle: {
    color: '#0088ff',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  lineItemsSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px',
    overflow: 'hidden',
  },
  lineItemsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  addButton: {
    background: 'rgba(0,102,204,0.2)',
    border: '1px solid rgba(0,102,204,0.4)',
    color: '#0088ff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  columnHeaders: {
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '6px 6px 0 0',
    fontSize: '10px',
    color: '#6080a0',
    textTransform: 'uppercase',
  },
  lineItemsList: {
    flex: 1,
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '0 0 6px 6px',
  },
  lineItem: {
    display: 'flex',
    gap: '8px',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'flex-start',
  },
  itemInput: {
    width: '100%',
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  descInput: {
    width: '100%',
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6080a0',
    fontSize: '11px',
    outline: 'none',
    marginTop: '4px',
  },
  numberInput: {
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'center',
  },
  dollarSign: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6080a0',
    fontSize: '12px',
  },
  priceInput: {
    width: '100%',
    padding: '6px 8px 6px 20px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'right',
    boxSizing: 'border-box',
  },
  lineTotal: {
    width: '90px',
    padding: '6px 0',
    color: '#0088ff',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'right',
  },
  deleteButton: {
    width: '32px',
    height: '32px',
    background: 'rgba(255,100,100,0.1)',
    border: 'none',
    borderRadius: '4px',
    color: '#ff6464',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#6080a0',
    fontSize: '13px',
  },
  sectionSubtotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: 'rgba(0,102,204,0.1)',
    borderRadius: '6px',
    color: '#0088ff',
    fontSize: '13px',
    fontWeight: '500',
    marginTop: '8px',
  },
  laborSection: {
    marginTop: '20px',
  },
  laborColumnHeaders: {
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '6px 6px 0 0',
    fontSize: '10px',
    color: '#6080a0',
    textTransform: 'uppercase',
  },
  laborItemsList: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '0 0 6px 6px',
  },
  laborItem: {
    display: 'flex',
    gap: '8px',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center',
  },
  laborPriceInput: {
    width: '100%',
    padding: '6px 8px 6px 20px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'right',
    boxSizing: 'border-box',
  },
  totalsSection: {
    marginTop: '16px',
    padding: '16px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '8px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    color: '#c0d0e0',
    fontSize: '14px',
  },
  totalValue: {
    fontWeight: '500',
  },
  taxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  taxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#c0d0e0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
  },
  taxInput: {
    width: '60px',
    padding: '4px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'right',
  },
  taxAmount: {
    marginLeft: 'auto',
    color: '#c0d0e0',
  },
  grandTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0 0 0',
    marginTop: '8px',
    borderTop: '2px solid rgba(0,102,204,0.4)',
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
  },
  grandTotalValue: {
    color: '#0088ff',
    fontSize: '20px',
  },
  companyPreview: {
    marginTop: '20px',
    padding: '12px',
    background: 'rgba(0,102,204,0.1)',
    border: '1px solid rgba(0,102,204,0.3)',
    borderRadius: '8px',
  },
  previewLabel: {
    fontSize: '10px',
    color: '#6080a0',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  previewCompany: {
    color: '#0088ff',
    fontSize: '14px',
    fontWeight: '600',
  },
  previewLicense: {
    color: '#6080a0',
    fontSize: '11px',
    marginTop: '4px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  // Client selector styles
  clientSelect: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(0,102,204,0.1)',
    border: '1px solid rgba(0,102,204,0.3)',
    borderRadius: '6px',
    color: '#0088ff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
};

export default InvoiceEditor;
